import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import useFundAccess, { fundPeopleUrl, myFundAccessUrl } from '@/hooks/useFundAccess';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { postAuth } from '@/api/ApiCalls';
import { FundPersonDtoOut } from '@/models/out/FundPersonDtoOut';
import { describeWorlds, worldsSummary } from '@/helpers/permissionWorlds';
import { EyeIcon } from '@/pages/FundSettings/people/peopleIcons';
import { PermissionsExplainerDialog } from '@/pages/FundSettings/people/PeopleDialogs';
import peopleStyles from '@/pages/FundSettings/people/People.module.css';
import styles from './ViewOnlyChip.module.css';

const fullName = (p: FundPersonDtoOut) => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();

/**
 * «צפייה בלבד» beside the fund's name in the bar on top of every screen (15.10.2026). Until now a
 * view-only coordinator learned it only when a form they had filled failed at the end. The chip
 * opens a short card: what view-only allows, who the founders are, and the request for full
 * permission - asked right there, so nobody has to find «אנשי הפרויקט» first.
 *
 * Since the permission worlds (22.10.2026) a coordinator with a mix - edit here, view or nothing
 * there - gets the chip too, as «מותאמת», and the card spells the mix out world by world.
 */
const ViewOnlyChip = ({ fundId }: { fundId: number }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { access, worlds } = useFundAccess(fundId);
  const summary = worldsSummary(worlds);
  const isCustom = summary === 'custom';
  const isPeopleOn = useFeature('fund-representatives');
  const [open, setOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  // The founders' names, read only once the card is open.
  const { data: people } = useGetAuthApi<FundPersonDtoOut[]>(open ? fundPeopleUrl(fundId) : '');
  const founders = (people ?? []).filter((p) => p.role === 'Founder' && p.founderStatus === 'Active');
  const names = founders.map(fullName).filter(Boolean).join(', ');

  const request = useMutation({
    mutationFn: () =>
      postAuth<unknown, object>(`${fundPeopleUrl(fundId)}/request-full-permission`, { Authorization: `Bearer ${currentUser?.token}` }, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [myFundAccessUrl(fundId)] });
      queryClient.invalidateQueries({ queryKey: [fundPeopleUrl(fundId)] });
    },
  });

  if (!access || access.isOffice || access.role === 'Founder' || summary === 'full') return null;

  const requested = access?.hasPendingPermissionRequest || request.isSuccess;
  const requestError = request.isError
    ? axios.isAxiosError(request.error) && typeof request.error.response?.data === 'string' && request.error.response.data.trim()
      ? request.error.response.data
      : t('fundPeople.genericError')
    : null;

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`${peopleStyles.chip} ${isCustom ? peopleStyles.chipCustom : peopleStyles.chipViewOnly} ${styles.trigger}`}
            aria-label={t(isCustom ? 'fundPeople.customCard.title' : 'fundPeople.viewOnlyCard.title')}
          >
            <EyeIcon />
            {t(isCustom ? 'fundPeople.permissionCustom' : 'fundPeople.permissionViewOnly')}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={styles.card} align="start" sideOffset={8} collisionPadding={12}>
            <span className={styles.title}>{t(isCustom ? 'fundPeople.customCard.title' : 'fundPeople.viewOnlyCard.title')}</span>
            {isCustom ? (
              <>
                <p className={styles.text}>{describeWorlds(worlds, t)}</p>
                <p className={styles.text}>{names ? t('fundPeople.customCard.textNamed', { names }) : t('fundPeople.customCard.text')}</p>
              </>
            ) : (
              <p className={styles.text}>{names ? t('fundPeople.viewOnlyCard.textNamed', { names }) : t('fundPeople.viewOnlyCard.text')}</p>
            )}
            {requested && (
              <p className={styles.sent}>{founders.length > 0 || !people ? t('fundPeople.viewOnly.sent') : t('fundPeople.viewOnly.sentWaiting')}</p>
            )}
            {requestError && <p className={styles.error}>{requestError}</p>}
            <div className={styles.actions}>
              {/* Without «אנשי הפרויקט» no founder has a screen to approve it on, so it is not offered. */}
              {isPeopleOn && !requested && (
                <button
                  type="button"
                  className={`${peopleStyles.pillButton} ${peopleStyles.pillPrimary} ${styles.ask}`}
                  disabled={request.isPending}
                  onClick={() => request.mutate()}
                >
                  {request.isPending ? t('loading') : t('fundPeople.viewOnly.ask')}
                </button>
              )}
              <button
                type="button"
                className={peopleStyles.explainLink}
                onClick={() => {
                  setOpen(false);
                  setExplainerOpen(true);
                }}
              >
                {t('fundPeople.explainLink')}
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <PermissionsExplainerDialog open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </>
  );
};

export default ViewOnlyChip;
